import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateRoomRequest, UpdateRoomRequest } from '@kreart/shared';
import { roomsApi } from './api';
import { invalidateProjectBom, projectsQueryKeys } from '../hooks';

const ROOT = ['project-rooms'] as const;
export const roomsQueryKeys = {
  all: ROOT,
  list: (projectId: string) => [...ROOT, 'list', projectId] as const,
};

export function useRooms(projectId: string | undefined) {
  return useQuery({
    queryKey: roomsQueryKeys.list(projectId ?? 'nil'),
    queryFn: () => roomsApi.list(projectId!),
    enabled: Boolean(projectId),
    meta: { silent: true },
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, projectId: string) {
  void qc.invalidateQueries({ queryKey: roomsQueryKeys.list(projectId) });
}

export function useCreateRoom(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRoomRequest) => roomsApi.create(projectId, body),
    meta: { silent: true },
    onSuccess: () => invalidate(qc, projectId),
  });
}

export function useUpdateRoom(projectId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateRoomRequest) => roomsApi.update(projectId, id, body),
    meta: { silent: true },
    onSuccess: () => invalidate(qc, projectId),
  });
}

export function useDeleteRoom(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => roomsApi.remove(projectId, id),
    meta: { silent: true },
    onSuccess: () => invalidate(qc, projectId),
  });
}

const bomExclusionsKey = (projectId: string, roomId: string) =>
  [...ROOT, 'bom-exclusions', projectId, roomId] as const;

/** BOM rows this room excludes from the detailed offer PDF. */
export function useBomExclusions(projectId: string, roomId: string | undefined) {
  return useQuery({
    queryKey: bomExclusionsKey(projectId, roomId ?? 'nil'),
    queryFn: () => roomsApi.getBomExclusions(projectId, roomId!),
    enabled: Boolean(roomId),
    meta: { silent: true },
  });
}

/** Replace a room's BOM exclusion set, optimistically updating the cached list. */
export function useSetBomExclusions(projectId: string, roomId: string) {
  const qc = useQueryClient();
  const key = bomExclusionsKey(projectId, roomId);
  return useMutation({
    mutationFn: (vars: { excludedKeys: string[]; includedKeys: string[] }) =>
      roomsApi.setBomExclusions(projectId, roomId, vars.excludedKeys, vars.includedKeys),
    meta: { silent: true },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData(key);
      qc.setQueryData(key, vars);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) qc.setQueryData(key, context.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useCopyRoom(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => roomsApi.copy(projectId, id),
    meta: { silent: true },
    onSuccess: () => {
      invalidate(qc, projectId);
      // Copying a room clones every line item, so every item list is stale.
      // Drawers, elements and cabinet hardware are cloned too and were missing
      // from this list.
      for (const key of [
        'project-cabinets',
        'project-panels',
        'project-countertops',
        'project-drawers',
        'project-elements',
        'project-hardware',
        'project-cabinet-hardware',
        'project-lighting',
      ]) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
      // The clone moves the BOM grand total and the project totals, so the
      // detail view and the tender must be refreshed as well — this path
      // bypassed invalidateProjectBom entirely.
      void invalidateProjectBom(qc, projectId);
      void qc.invalidateQueries({ queryKey: projectsQueryKeys.detail(projectId) });
    },
  });
}
